import { useState } from "react";
import { Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { ShoppingBag, Minus, Plus, Trash2, Loader2, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { formatPrice, postCheckout, getVisitorSessionId } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";

export const CartDrawer = ({ triggerClassName = "text-foreground hover:bg-secondary" }) => {
  const { items, count, subtotal, currency, updateQty, removeItem, open, setOpen } = useCart();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const lines = items.map((i) => ({ variant_id: i.variantId, quantity: i.quantity }));
      const primaryBrand = items[0]?.brand?.toLowerCase().includes("dy") ? "dyon" : "kentucky";
      const res = await postCheckout({ lines, brand: primaryBrand, utm_campaign: "kentucky_cart", session_id: getVisitorSessionId() });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 503 ? "Store is reconnecting" : "Checkout unavailable",
        { description: "Please try again in a moment, or shop on Boomerang.ie." }
      );
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Open cart, ${count} items`}
          data-testid="cart-open-button"
          className={`relative ${triggerClassName}`}
        >
          <ShoppingBag className="h-5 w-5" />
          {count > 0 && (
            <span
              data-testid="cart-count-badge"
              className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-boomerang px-1 text-[10px] font-bold leading-none text-white"
            >
              {count}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="h-5 w-5 text-boomerang" /> {t("cart.title")}
            {count > 0 && <span className="text-sm font-normal text-muted-foreground">({count})</span>}
          </SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping cart. Review quantities and proceed to secure checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cart-empty-state">
            <div className="rounded-full bg-secondary p-5">
              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-display text-lg">{t("cart.empty_title")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("cart.empty_desc")}</p>
            </div>
            <Button asChild onClick={() => setOpen(false)} data-testid="cart-empty-shop-button">
              <LangLink to="/brand/kentucky">{t("cart.shop_kentucky")} <ArrowRight className="ml-2 h-4 w-4" /></LangLink>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <ul className="divide-y divide-border">
                {items.map((item) => (
                  <li key={item.variantId} className="flex gap-3 py-4" data-testid="cart-line-item">
                    <LangLink
                      to={`/product/${item.handle}`}
                      onClick={() => setOpen(false)}
                      className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-white"
                    >
                      {item.image ? (
                        <img src={item.image} alt={item.title} className="h-full w-full object-contain p-1" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[10px] text-muted-foreground">No image</div>
                      )}
                    </LangLink>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <LangLink
                          to={`/product/${item.handle}`}
                          onClick={() => setOpen(false)}
                          className="line-clamp-2 text-sm font-medium leading-snug hover:text-boomerang"
                        >
                          {item.title}
                        </LangLink>
                        <button
                          aria-label="Remove item"
                          data-testid="cart-remove-button"
                          onClick={() => removeItem(item.variantId)}
                          className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {item.variantTitle && item.variantTitle !== "Default Title" && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{item.variantTitle}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center rounded-md border border-border">
                          <button
                            aria-label="Decrease quantity"
                            data-testid="cart-qty-decrease"
                            onClick={() => updateQty(item.variantId, item.quantity - 1)}
                            className="px-2 py-1.5 text-muted-foreground hover:text-foreground"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-[28px] text-center text-sm font-medium" data-testid="cart-qty-value">{item.quantity}</span>
                          <button
                            aria-label="Increase quantity"
                            data-testid="cart-qty-increase"
                            onClick={() => updateQty(item.variantId, item.quantity + 1)}
                            disabled={Number.isFinite(item.maxQty) && item.quantity >= item.maxQty}
                            className="px-2 py-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="text-sm font-semibold">
                          {formatPrice({ amount: (item.price?.amount || 0) * item.quantity, currency })}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <SheetFooter className="flex-col gap-3 border-t border-border px-5 py-4 sm:flex-col sm:space-x-0">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("cart.subtotal")}</span>
                <span className="font-display text-lg" data-testid="cart-subtotal">{formatPrice({ amount: subtotal, currency })}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("cart.note")}
              </p>
              <Button
                size="lg"
                className="w-full"
                onClick={handleCheckout}
                disabled={loading}
                data-testid="cart-checkout-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("cart.redirecting")}</>
                ) : (
                  <><Lock className="mr-2 h-4 w-4" /> {t("cart.checkout")}</>
                )}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
